import { useEffect, useRef, useState, useMemo } from 'react';
import { Paper, Group, Text, Select, SegmentedControl, ActionIcon, Tooltip, Progress } from '@mantine/core';
import Download from 'lucide-react/dist/esm/icons/download';
import Trash2 from 'lucide-react/dist/esm/icons/trash-2';
import { useAuth } from '../../../shared/AuthContext';
import type { LogEntry, LogLevel } from '../types';
import { LEVEL_OPTIONS, LEVEL_RANK } from '../utils';
import classes from '../editor.module.css';

interface Props {
  logEntries: LogEntry[]; logLevel: LogLevel; onLogLevelChange: (level: LogLevel) => void;
  onClearLogs: () => void; overallProgress?: { rendered: string; total: string; percent: number } | null;
}

export function LogPanel({ logEntries, logLevel, onLogLevelChange, onClearLogs, overallProgress }: Props) {
  const auth = useAuth();
  const logRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<'events' | 'ffmpeg'>('events');
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; }, [logEntries, activeTab]);

  const { counts, ffCount, evFilteredCount, displayed } = useMemo(() => {
    const counts: Record<LogLevel, number> = { debug: 0, info: 0, warning: 0, error: 0 };
    let ff = 0, evF = 0; const disp: LogEntry[] = [];
    for (const e of logEntries) {
      if (e.source === 'ffmpeg' || e.kind === 'ffmpeg') {
        ff++; if (activeTab === 'ffmpeg') disp.push(e);
      } else {
        counts[e.level] = (counts[e.level] || 0) + 1;
        if (LEVEL_RANK[e.level] >= LEVEL_RANK[logLevel]) {
          evF++; if (activeTab === 'events') disp.push(e);
        }
      }
    }
    return { counts, ffCount: ff, evFilteredCount: evF, displayed: disp };
  }, [logEntries, activeTab, logLevel]);

  async function handleDownloadFullLog() {
    try {
      setIsDownloading(true);
      const { token } = await auth.identity();
      const rsp = await fetch('/api/editor/logs/ffmpeg', { headers: { authorization: `Bearer ${token}` } });
      if (!rsp.ok) throw new Error('Лог не найден');
      const blob = await rsp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `ffmpeg_preview_${Date.now()}.log`; a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) { alert(e?.message ?? 'Не удалось скачать лог'); }
    finally { setIsDownloading(false); }
  }

  return (
    <Paper withBorder radius="md" mt="lg" style={{ overflow: 'hidden' }}>
      <Group justify="space-between" px="md" py="xs" style={{ background: 'var(--mantine-color-default-hover)', borderBottom: '1px solid var(--mantine-color-default-border)' }}>
        <SegmentedControl size="xs" value={activeTab} onChange={v => setActiveTab(v as any)} data={[{ value: 'events', label: `События (${evFilteredCount})` }, { value: 'ffmpeg', label: `FFmpeg (${ffCount})` }]} />
        <Group gap="xs" align="center">
          {activeTab === 'events' && (
            <Select size="xs" w={140} data={LEVEL_OPTIONS.map(o => ({ value: o.value, label: `${o.label} (${counts[o.value as LogLevel] || 0})` }))} value={logLevel} onChange={v => onLogLevelChange((v as LogLevel) ?? 'info')} allowDeselect={false} />
          )}
          <Tooltip label="Скачать полный лог для отчёта об ошибке" withArrow><ActionIcon variant="subtle" color="gray" size="sm" loading={isDownloading} onClick={handleDownloadFullLog}><Download size={15} /></ActionIcon></Tooltip>
          <Tooltip label="Очистить лог" withArrow><ActionIcon variant="subtle" color="gray" size="sm" onClick={onClearLogs}><Trash2 size={15} /></ActionIcon></Tooltip>
        </Group>
      </Group>

      {overallProgress && (
        <div style={{ padding: '6px 14px', background: 'var(--mantine-color-default)', borderBottom: '1px solid var(--mantine-color-default-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <Group gap="xs" wrap="nowrap"><Text fz="xs" fw={700} c="brand">Хронометраж:</Text><Text fz="xs" fw={600} style={{ fontVariantNumeric: 'tabular-nums' }}>{overallProgress.rendered} / {overallProgress.total}</Text></Group>
          <Group gap="xs" style={{ flex: 1, maxWidth: 300 }} wrap="nowrap">
            <div style={{ flex: 1 }}><Progress value={overallProgress.percent} size="sm" radius="xl" color={overallProgress.percent >= 100 ? "green" : "brand"} animated={overallProgress.percent < 100} /></div>
            <Text fz="xs" c={overallProgress.percent >= 100 ? "green" : "dimmed"} w={42} ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>{overallProgress.percent}%</Text>
          </Group>
        </div>
      )}

      <div className={classes.log} ref={logRef} style={activeTab === 'ffmpeg' ? { fontFamily: 'monospace', fontSize: 12 } : undefined}>
        {displayed.map((e, i) => (
          <div key={i} className={classes.logLine}><span className={classes.logTime}>{e.ts}</span><span className={classes.logMsg} data-level={e.level} data-kind={e.kind}>{e.message}</span></div>
        ))}
        {displayed.length === 0 && <Text c="dimmed" fz="xs" ta="center" py="xl">{activeTab === 'ffmpeg' ? 'Вывод FFmpeg пуст' : 'Нет событий'}</Text>}
      </div>
    </Paper>
  );
}
